import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { customerApi, scheduleApi } from '../api';
import {
  CUSTOMER_STATUSES, CUSTOMER_STATUS_COLORS, GENDERS,
  REFERRAL_SOURCES, REFERRAL_SOURCE_DETAILS,
} from '../constants';

const CUSTOMER_TYPES = [
  { value: 'RENTAL', label: '대여' },
  { value: 'CUSTOM', label: '맞춤' },
];

interface DressCustomer {
  dressCustomerIdx: number;
  name: string;
  phone: string;
  email?: string;
  gender?: string;
  status: string;
  rehearsalDt?: string;
  weddingDt?: string;
  contractDt?: string;
  shootingDt?: string;
  weddingVenue?: string;
  studioName?: string;
  dressSelectInfo?: string;
  referralSource?: string;
  isParent?: string;
  partnerVendorNames?: string;
  suit?: { ordered: boolean; released: boolean; status?: string };
  shirt?: { ordered: boolean; released: boolean; status?: string };
  shoes?: { ordered: boolean; released: boolean; status?: string };
  assignedStaff?: { userIdx: number; name: string };
  createdDt: string;
}

export default function DressCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<DressCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  const [form, setForm] = useState({
    name: '', phone: '', email: '', gender: '', customerType: '',
    weddingDt: '', rehearsalDt: '', contractDt: '', shootingDt: '', isParent: 'n',
    referralSource: '', referralSourceDetail: '',
    status: '상담접수', memo: '',
    studioName: '', weddingVenue: '', dressSelectInfo: '',
  });

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await customerApi.list({
        q: searchQuery, status: statusFilter,
        dateFrom: dateFrom || undefined, dateTo: dateTo || undefined,
        vendorName: vendorName || undefined, page, size: 20,
      });
      setCustomers(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('고객 목록 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, statusFilter, dateFrom, dateTo, vendorName, page]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone) { alert('고객명과 연락처는 필수입니다.'); return; }
    try {
      const { contractDt: _contractDt, shootingDt: _shootingDt, ...customerData } = form;
      const customer = await customerApi.create(customerData);
      const custIdx = customer?.dressCustomerIdx;

      // 예식일, 리허설일, 계약일, 촬영일 자동 일정 등록
      if (custIdx) {
        const schedulePromises: Promise<any>[] = [];
        if (form.weddingDt) {
          schedulePromises.push(scheduleApi.create({
            section: 'STORE',
            scheduleCategory: 'WEDDING',
            title: `${form.name} 예식`,
            startDt: form.weddingDt,
            endDt: form.weddingDt,
            dressCustomerIdx: custIdx,
            customerName: form.name,
            customerPhone: form.phone,
          }).catch(() => {}));
        }
        if (form.rehearsalDt) {
          schedulePromises.push(scheduleApi.create({
            section: 'STORE',
            scheduleCategory: 'REHEARSAL',
            title: `${form.name} 리허설`,
            startDt: form.rehearsalDt,
            endDt: form.rehearsalDt,
            dressCustomerIdx: custIdx,
            customerName: form.name,
            customerPhone: form.phone,
          }).catch(() => {}));
        }
        if (form.contractDt) {
          schedulePromises.push(scheduleApi.create({
            section: 'STORE',
            scheduleCategory: 'CONSULTATION',
            title: `${form.name} 계약`,
            startDt: form.contractDt,
            endDt: form.contractDt,
            dressCustomerIdx: custIdx,
            customerName: form.name,
            customerPhone: form.phone,
          }).catch(() => {}));
        }
        if (form.shootingDt) {
          schedulePromises.push(scheduleApi.create({
            section: 'STORE',
            scheduleCategory: 'SHOOTING',
            title: `${form.name} 촬영`,
            startDt: form.shootingDt,
            endDt: form.shootingDt,
            dressCustomerIdx: custIdx,
            customerName: form.name,
            customerPhone: form.phone,
          }).catch(() => {}));
        }
        await Promise.all(schedulePromises);
      }

      alert('고객이 등록되었습니다.');
      setShowAddModal(false);
      setForm({
        name: '', phone: '', email: '', gender: '', customerType: '',
        weddingDt: '', rehearsalDt: '', contractDt: '', shootingDt: '', isParent: 'n',
        referralSource: '', referralSourceDetail: '',
        status: '상담접수', memo: '',
        studioName: '', weddingVenue: '', dressSelectInfo: '',
      });
      fetchCustomers();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  const renderItemBadge = (item?: { ordered: boolean; released: boolean }) => {
    if (!item) return <span className="text-xs text-gray-300">-</span>;
    if (item.released) return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">출고</span>;
    if (item.ordered) return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">발주</span>;
    return <span className="text-xs text-gray-300">-</span>;
  };

  const formatDate = (dt?: string) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  const daysUntil = (dt?: string) => {
    if (!dt) return null;
    const diff = Math.ceil((new Date(dt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return null;
    return diff;
  };

  const totalPages = Math.ceil(total / 20);
  const referralDetailOptions = REFERRAL_SOURCE_DETAILS[form.referralSource] || [];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">

        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">고객관리</h1>
            <p className="text-sm text-gray-600 mt-1">드레스샵 상담/계약 고객을 관리합니다</p>
          </div>
          <button onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 cursor-pointer">
            <i className="ri-add-line"></i> 고객 등록
          </button>
        </div>

        {/* 필터 영역 */}
        <div className="bg-white p-5 rounded-lg shadow-sm mb-6">
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">상태</label>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]">
                <option value="">전체</option>
                {CUSTOMER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">검색기간</label>
              <div className="flex items-center gap-2">
                <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <span className="text-gray-400">~</span>
                <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">발주업체</label>
              <input type="text" placeholder="업체명 검색" value={vendorName}
                onChange={(e) => { setVendorName(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[160px]" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">검색</label>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input type="text" placeholder="고객명 / 전화번호 검색" value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>
        </div>

        {/* 검색 결과 카운트 */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-gray-600">
            총 <span className="font-semibold text-gray-800">{total}</span>명의 고객
          </div>
        </div>

        {/* 고객 목록 */}
        {isLoading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : customers.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 text-center text-gray-400">
            <i className="ri-user-3-line text-5xl block mb-3"></i>등록된 고객이 없습니다.
          </div>
        ) : (
          <div className="space-y-3">
            {customers.map((c) => {
              const weddingDays = daysUntil(c.weddingDt);
              return (
                <div key={c.dressCustomerIdx}
                  className="bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => navigate(`/dress-shop/customer-detail?id=${c.dressCustomerIdx}`)}>
                  <div className="flex items-center gap-5">
                    {/* 프로필 */}
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-100 to-pink-50 flex items-center justify-center text-base font-bold text-pink-600 group-hover:from-pink-200 group-hover:to-pink-100 transition-colors flex-shrink-0">
                      {c.name.charAt(0)}
                    </div>

                    {/* 고객 기본 정보 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base font-semibold text-gray-900">{c.name}</span>
                        {c.isParent === 'y' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 font-medium">혼주</span>}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${CUSTOMER_STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-700'}`}>{c.status}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span><i className="ri-phone-line mr-1"></i>{c.phone}</span>
                        {c.assignedStaff?.name && <span><i className="ri-user-star-line mr-1"></i>{c.assignedStaff.name}</span>}
                        {c.partnerVendorNames && <span><i className="ri-building-line mr-1"></i>{c.partnerVendorNames}</span>}
                      </div>
                    </div>

                    {/* 일정 정보 */}
                    <div className="flex items-center gap-6 flex-shrink-0">
                      <div className="text-center">
                        <div className="text-[10px] text-gray-400 mb-0.5">계약일</div>
                        <div className="text-xs font-medium text-gray-700">{formatDate(c.contractDt)}</div>
                      </div>
                      {c.shootingDt && (
                        <div className="text-center">
                          <div className="text-[10px] text-gray-400 mb-0.5">촬영일</div>
                          <div className="text-xs font-medium text-gray-700">{formatDate(c.shootingDt)}</div>
                        </div>
                      )}
                      <div className="text-center">
                        <div className="text-[10px] text-gray-400 mb-0.5">리허설</div>
                        <div className="text-xs font-medium text-gray-700">{formatDate(c.rehearsalDt)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] text-gray-400 mb-0.5">예식일</div>
                        <div className="text-xs font-medium text-gray-700">{formatDate(c.weddingDt)}</div>
                        {weddingDays !== null && (
                          <div className={`text-[10px] font-bold ${weddingDays <= 14 ? 'text-red-500' : weddingDays <= 30 ? 'text-orange-500' : 'text-gray-400'}`}>
                            D-{weddingDays}
                          </div>
                        )}
                      </div>
                    </div>

                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-blue-500 text-xl transition-colors flex-shrink-0"></i>
                  </div>

                  {/* 품목 + 결제 현황 */}
                  <div className="px-5 pb-4 pt-1 flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      {(c.purchaseItems || []).map((item: any, idx: number) => {
                        const statusColor = item.workStatus && item.workStatus !== '미배정'
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-gray-50 text-gray-600 border-gray-200';
                        return (
                          <span key={idx} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border ${statusColor}`}>
                            <span className="font-medium">{item.productName}</span>
                            {item.workStatus && item.workStatus !== '미배정' && (
                              <span className="text-[10px] opacity-75">{item.workStatus}</span>
                            )}
                          </span>
                        );
                      })}
                      {(!c.purchaseItems || c.purchaseItems.length === 0) && (
                        <span className="text-xs text-gray-300">등록된 품목 없음</span>
                      )}
                    </div>
                    {c.totalContractAmount > 0 && (
                      <div className="flex items-center gap-4 flex-shrink-0 text-xs">
                        <span className="text-gray-500">계약 <span className="font-semibold text-gray-800">{c.totalContractAmount.toLocaleString()}원</span></span>
                        <span className="text-blue-600">납부 <span className="font-semibold">{c.totalPaidAmount.toLocaleString()}원</span></span>
                        {c.remainingAmount > 0 && (
                          <span className="text-red-500">미납 <span className="font-bold">{c.remainingAmount.toLocaleString()}원</span></span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg text-sm cursor-pointer ${page === p ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-600'}`}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}

        {/* 고객 등록 모달 */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">신규 고객 등록</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-6">
                {/* 기본 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-user-line text-gray-400"></i> 기본 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">고객명 *</label>
                      <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">연락처 *</label>
                      <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="010-0000-0000" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                      <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">성별</label>
                      <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {GENDERS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">고객 구분</label>
                      <select value={form.customerType} onChange={(e) => setForm({ ...form, customerType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {CUSTOMER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                      <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        {CUSTOMER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">혼주 여부</label>
                      <select value={form.isParent} onChange={(e) => setForm({ ...form, isParent: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="n">N</option>
                        <option value="y">Y</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* 드레스/촬영 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-shirt-line text-gray-400"></i> 드레스/촬영 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">스튜디오명</label>
                      <input type="text" value={form.studioName} onChange={(e) => setForm({ ...form, studioName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="스튜디오명 입력" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">본식장소</label>
                      <input type="text" value={form.weddingVenue} onChange={(e) => setForm({ ...form, weddingVenue: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="본식장소 입력" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">드레스셀렉정보</label>
                      <textarea value={form.dressSelectInfo} onChange={(e) => setForm({ ...form, dressSelectInfo: e.target.value })} rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        placeholder="드레스 셀렉 관련 정보를 입력하세요" />
                    </div>
                  </div>
                </div>

                {/* 일정 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-calendar-heart-line text-gray-400"></i> 일정 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예식일</label>
                      <input type="datetime-local" value={form.weddingDt} onChange={(e) => setForm({ ...form, weddingDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">리허설일</label>
                      <input type="datetime-local" value={form.rehearsalDt} onChange={(e) => setForm({ ...form, rehearsalDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">계약일</label>
                      <input type="datetime-local" value={form.contractDt} onChange={(e) => setForm({ ...form, contractDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">촬영일</label>
                      <input type="datetime-local" value={form.shootingDt} onChange={(e) => setForm({ ...form, shootingDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>

                {/* 유입/기타 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-route-line text-gray-400"></i> 유입 경로
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">유입경로</label>
                      <select value={form.referralSource}
                        onChange={(e) => setForm({ ...form, referralSource: e.target.value, referralSourceDetail: '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {REFERRAL_SOURCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">유입경로 상세</label>
                      {referralDetailOptions.length > 0 ? (
                        <select value={form.referralSourceDetail}
                          onChange={(e) => setForm({ ...form, referralSourceDetail: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">선택</option>
                          {referralDetailOptions.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                      ) : (
                        <input type="text" value={form.referralSourceDetail}
                          onChange={(e) => setForm({ ...form, referralSourceDetail: e.target.value })}
                          placeholder={form.referralSource ? '직접 입력' : '유입경로를 먼저 선택하세요'}
                          disabled={!form.referralSource}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button type="button" onClick={() => setShowAddModal(false)}
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium cursor-pointer">취소</button>
                  <button type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
